/**
 * Meeting Service Hooks
 * React hooks for accessing meeting management services
 */
import { useCCMS } from '../useCCMS';
/**
 * Hook to access all meeting-related services
 * @returns MeetingServices object containing all meeting service instances
 */
export function useMeetingsServices() {
    const { services } = useCCMS();
    if (!services?.meetings) {
        throw new Error('Meeting services not available. Ensure CCMSProvider is configured with meeting services.');
    }
    return services.meetings;
}
/**
 * Hook to access MeetingService
 */
export function useMeetingService() {
    return useMeetingsServices().meeting;
}
/**
 * Hook to access MeetingStatusService
 */
export function useMeetingStatusService() {
    return useMeetingsServices().meetingStatus;
}
/**
 * Hook to access MeetingTypeService
 */
export function useMeetingTypeService() {
    return useMeetingsServices().meetingType;
}
/**
 * Hook to access MeetingVenueService
 */
export function useMeetingVenueService() {
    return useMeetingsServices().meetingVenue;
}
/**
 * Hook to access MeetingAttendeeService
 */
export function useMeetingAttendeeService() {
    return useMeetingsServices().meetingAttendee;
}
/**
 * Hook to access MeetingAttendanceStatusService
 */
export function useMeetingAttendanceStatusService() {
    return useMeetingsServices().meetingAttendanceStatus;
}
/**
 * Hook to access MeetingSetupService
 */
export function useMeetingSetupService() {
    return useMeetingsServices().meetingSetup;
}
/**
 * Hook to access MeetingRequestService
 */
export function useMeetingRequestService() {
    return useMeetingsServices().meetingRequest;
}
/**
 * Hook to access MeetingCommentService
 */
export function useMeetingCommentService() {
    return useMeetingsServices().meetingComment;
}
/**
 * Hook to access MeetingFileService
 */
export function useMeetingFileService() {
    return useMeetingsServices().meetingFile;
}
/**
 * Hook to access SetupInMeetingService
 */
export function useSetupInMeetingService() {
    return useMeetingsServices().setupInMeeting;
}
/**
 * Hook to access RequestInMeetingService
 */
export function useRequestInMeetingService() {
    return useMeetingsServices().requestInMeeting;
}
/**
 * Hook to access LocationVenueRequestService
 */
export function useLocationVenueRequestService() {
    return useMeetingsServices().locationVenueRequest;
}
/**
 * Hook to access LocationVenueSetupService
 */
export function useLocationVenueSetupService() {
    return useMeetingsServices().locationVenueSetup;
}
/**
 * Hook to access MeetingSettingsService
 */
export function useMeetingSettingsService() {
    return useMeetingsServices().meetingSettings;
}
/**
 * Hook to access VenueBookingService
 */
export function useVenueBookingService() {
    return useMeetingsServices().venueBooking;
}
/**
 * Hook to access VenueAvailabilityService
 */
export function useVenueAvailabilityService() {
    return useMeetingsServices().venueAvailability;
}
/**
 * Hook to access VenueBlackoutService
 */
export function useVenueBlackoutService() {
    return useMeetingsServices().venueBlackout;
}
/**
 * Hook to access VenueSetupLayoutService
 */
export function useVenueSetupLayoutService() {
    return useMeetingsServices().venueSetupLayout;
}
/**
 * Hook to access MeetingRecurrenceService
 */
export function useMeetingRecurrenceService() {
    return useMeetingsServices().meetingRecurrence;
}
/**
 * Hook to access MeetingAgendaItemService
 */
export function useMeetingAgendaItemService() {
    return useMeetingsServices().meetingAgendaItem;
}
/**
 * Hook to access MeetingMinutesService
 */
export function useMeetingMinutesService() {
    return useMeetingsServices().meetingMinutes;
}
/**
 * Hook to access MeetingActionItemService
 */
export function useMeetingActionItemService() {
    return useMeetingsServices().meetingActionItem;
}
/**
 * Hook to access VenueBookingStatusService
 */
export function useVenueBookingStatusService() {
    return useMeetingsServices().venueBookingStatus;
}
/**
 * Hook to access MeetingActionItemStatusService
 */
export function useMeetingActionItemStatusService() {
    return useMeetingsServices().meetingActionItemStatus;
}
/**
 * Hook to access SeatingStyleService
 */
export function useSeatingStyleService() {
    return useMeetingsServices().seatingStyle;
}
//# sourceMappingURL=useMeetingsServices.js.map